import React from 'react';
import { Player } from '../../types';
import { PlayerListItem } from './PlayerListItem';

export interface PlayerPanelProps {
  players: Player[];
  currentUserId: string;
  currentTurnPlayerId: string | null;
  selectedPlayerId: string | null;
  onPlayerSelect: (playerId: string) => void;
}

/**
 * PlayerPanel Component
 * 
 * A narrow left-hand panel displaying a scrollable list of all players in the room.
 * Features:
 * - Scrollable list of PlayerListItem components
 * - Player selection with onClick callback
 * - Turn indicator in header
 * - Fixed width of 280px max
 * 
 * Requirements: 2.1, 1.4, 8.1
 */
export const PlayerPanel: React.FC<PlayerPanelProps> = ({
  players,
  currentUserId,
  currentTurnPlayerId,
  selectedPlayerId,
  onPlayerSelect,
}) => {
  // Find the current turn player for the header display
  const currentTurnPlayer = players.find(p => p.userId === currentTurnPlayerId);
  
  // Sort players: current user first, then by turn order, eliminated last
  const sortedPlayers = [...players].sort((a, b) => {
    // Current user always first
    if (a.userId === currentUserId) return -1;
    if (b.userId === currentUserId) return 1;
    
    // Eliminated players last
    if (a.stats.isEliminated && !b.stats.isEliminated) return 1;
    if (!a.stats.isEliminated && b.stats.isEliminated) return -1;
    
    // Current turn player second
    if (a.userId === currentTurnPlayerId) return -1;
    if (b.userId === currentTurnPlayerId) return 1;
    
    return 0;
  });

  return (
    <div 
      className="player-panel flex flex-col h-full w-full max-w-[280px] bg-gray-900/50 border-r border-gray-700"
      role="region"
      aria-label="Players panel"
    >
      {/* Panel Header with Turn Indicator (Requirement 8.1) */}
      <div className="player-panel-header p-4 border-b border-gray-700 bg-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-2">Players</h2>
        
        {/* Turn indicator (Requirement 8.1) */}
        {currentTurnPlayer && !currentTurnPlayer.stats.isEliminated && (
          <div className="turn-indicator bg-green-500/20 border border-green-500/30 rounded-lg p-2">
            <div className="text-xs text-green-400 uppercase tracking-wide font-medium">
              Current Turn
            </div>
            <div className="text-sm text-white font-medium truncate">
              {currentTurnPlayer.userId === currentUserId 
                ? 'Your Turn!' 
                : currentTurnPlayer.username
              }
            </div>
            {currentTurnPlayer.stats.phasesRemaining > 0 && (
              <div className="text-xs text-green-300 mt-1">
                {currentTurnPlayer.stats.phasesRemaining} phase{currentTurnPlayer.stats.phasesRemaining !== 1 ? 's' : ''} remaining
              </div>
            )}
          </div>
        )}
        
        {/* Player count */}
        <div className="text-xs text-gray-400 mt-2">
          {players.filter(p => !p.stats.isEliminated).length} / {players.length} active
        </div>
      </div>

      {/* Scrollable Player List (Requirement 2.1) */}
      <div 
        className="player-list flex-1 overflow-y-auto p-3 space-y-2"
        role="listbox"
        aria-label="Player list"
      >
        {sortedPlayers.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            No players in room
          </div>
        ) : (
          sortedPlayers.map((player) => (
            <PlayerListItem
              key={player.userId}
              player={player}
              isCurrentUser={player.userId === currentUserId}
              isCurrentTurn={player.userId === currentTurnPlayerId}
              isSelected={player.userId === selectedPlayerId}
              onClick={() => onPlayerSelect(player.userId)}
            />
          ))
        )}
      </div>
    </div>
  );
};
